import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Gear } from '@phosphor-icons/react';
import {
  createPalletType, updatePalletType, deletePalletType,
  getPalletAuditSettings, updatePalletAuditSettings,
} from '../../lib/api';

export default function TypesTab({ types, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', is_pooled: false, daily_hire_rate: 0, color: '#475569', claim_email: '',
  });
  const [saving, setSaving] = useState(false);

  const [auditSettings, setAuditSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  useEffect(() => {
    getPalletAuditSettings().then((r) => setAuditSettings(r.data)).catch(() => {});
  }, []);
  const saveAuditSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await updatePalletAuditSettings(auditSettings);
      setAuditSettings(data);
      toast.success('Investigator settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const beginCreate = () => {
    setEditing(null);
    setForm({ code: '', name: '', is_pooled: false, daily_hire_rate: 0, color: '#475569', claim_email: '' });
    setOpen(true);
  };
  const beginEdit = (t) => {
    setEditing(t);
    setForm({
      code: t.code, name: t.name, is_pooled: !!t.is_pooled,
      daily_hire_rate: Number(t.daily_hire_rate || 0),
      color: t.color || '#475569',
      claim_email: t.claim_email || '',
    });
    setOpen(true);
  };
  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!editing && !form.code.trim()) return toast.error('Code is required');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        is_pooled: !!form.is_pooled,
        daily_hire_rate: Number(form.daily_hire_rate || 0),
        color: form.color || null,
        claim_email: (form.claim_email || '').trim() || null,
      };
      if (editing) {
        await updatePalletType(editing.id, payload);
        toast.success('Pallet type updated');
      } else {
        await createPalletType({ ...payload, code: form.code.trim().toUpperCase() });
        toast.success('Pallet type created');
      }
      setOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };
  const onDelete = async (t) => {
    if (!window.confirm(`Delete pallet type "${t.name}"? Movements with this type stay intact.`)) return;
    try {
      await deletePalletType(t.id);
      toast.success('Pallet type removed');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };
  const toggleActive = async (t) => {
    try {
      await updatePalletType(t.id, { is_active: !t.is_active });
      toast.success(t.is_active ? 'Type deactivated' : 'Type reactivated');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Investigator settings */}
      {auditSettings && (
        <section className="border border-border bg-card p-4 space-y-3" data-testid="investigator-settings-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold flex items-center gap-2"><Gear size={14} weight="fill" /> Investigator settings</h3>
              <p className="text-[11px] text-muted-foreground">Drive the nightly recall chase, drift alerts and weekly digest without touching the DB.</p>
            </div>
            <Button size="sm" onClick={saveAuditSettings} disabled={savingSettings} data-testid="investigator-settings-save">
              {savingSettings ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div>
              <Label>Recall threshold (days)</Label>
              <Input type="number" min="1" max="365"
                value={auditSettings.pallet_recall_threshold_days ?? 30}
                onChange={(e) => setAuditSettings({ ...auditSettings, pallet_recall_threshold_days: Number(e.target.value) })}
                data-testid="setting-recall-threshold" />
              <p className="text-[10px] text-muted-foreground mt-1">Chase merchants when tickets age past this many days.</p>
            </div>
            <div>
              <Label>Drift alert drop %</Label>
              <Input type="number" min="1" max="100"
                value={auditSettings.pallet_drift_alert_drop_pct ?? 20}
                onChange={(e) => setAuditSettings({ ...auditSettings, pallet_drift_alert_drop_pct: Number(e.target.value) })}
                data-testid="setting-drift-drop-pct" />
              <p className="text-[10px] text-muted-foreground mt-1">Fire an alert if balance drops this % below 7-day avg.</p>
            </div>
            <div>
              <Label>Drift alert email</Label>
              <Input type="email" placeholder="ops@yourco.com"
                value={auditSettings.pallet_drift_alert_email || ''}
                onChange={(e) => setAuditSettings({ ...auditSettings, pallet_drift_alert_email: e.target.value })}
                data-testid="setting-drift-email" />
              <p className="text-[10px] text-muted-foreground mt-1">Falls back to owner email if blank.</p>
            </div>
            <div>
              <Label>Drift Slack webhook</Label>
              <Input type="url" placeholder="https://hooks.slack.com/services/…"
                value={auditSettings.pallet_drift_slack_webhook || ''}
                onChange={(e) => setAuditSettings({ ...auditSettings, pallet_drift_slack_webhook: e.target.value })}
                data-testid="setting-drift-slack" />
            </div>
            <div>
              <Label>Recall digest email</Label>
              <Input type="email" placeholder="ops@yourco.com"
                value={auditSettings.pallet_recall_digest_email || ''}
                onChange={(e) => setAuditSettings({ ...auditSettings, pallet_recall_digest_email: e.target.value })}
                data-testid="setting-digest-email" />
              <p className="text-[10px] text-muted-foreground mt-1">Summary of chases + returns.</p>
            </div>
            <div>
              <Label>Digest cadence</Label>
              <select
                className="w-full border border-border bg-background h-9 px-2 text-sm"
                value={auditSettings.pallet_recall_digest_cadence || 'weekly'}
                onChange={(e) => setAuditSettings({ ...auditSettings, pallet_recall_digest_cadence: e.target.value })}
                data-testid="setting-digest-cadence"
              >
                <option value="daily">Daily · every morning</option>
                <option value="weekly">Weekly · Monday morning</option>
                <option value="monthly">Monthly · 1st of the month</option>
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">How often the digest fires (fires at 09:00 UTC).</p>
            </div>
            <div>
              <Label>Recall digest Slack webhook</Label>
              <Input type="url" placeholder="https://hooks.slack.com/services/…"
                value={auditSettings.pallet_recall_slack_webhook || ''}
                onChange={(e) => setAuditSettings({ ...auditSettings, pallet_recall_slack_webhook: e.target.value })}
                data-testid="setting-digest-slack" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs pt-1">
            <input
              type="checkbox"
              checked={auditSettings.return_magic_links_enabled ?? true}
              onChange={(e) => setAuditSettings({ ...auditSettings, return_magic_links_enabled: e.target.checked })}
              data-testid="setting-magic-links"
            />
            <span>Embed a <b>Confirm return</b> magic-link in every recall email so merchants can tick off pallets in one click.</span>
          </label>

          {/* Dispute auto-file */}
          <div className="border-t border-border pt-3 mt-2">
            <h4 className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Dispute auto-file</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Dispute case email</Label>
                <Input type="email" placeholder="disputes@chep.com or your ops inbox"
                  value={auditSettings.pallet_dispute_email || ''}
                  onChange={(e) => setAuditSettings({ ...auditSettings, pallet_dispute_email: e.target.value })}
                  data-testid="setting-dispute-email" />
                <p className="text-[10px] text-muted-foreground mt-1">Receives a PDF pack when a merchant reports pallets as never received.</p>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs pb-2">
                  <input
                    type="checkbox"
                    checked={auditSettings.pallet_dispute_auto_file_enabled ?? true}
                    onChange={(e) => setAuditSettings({ ...auditSettings, pallet_dispute_auto_file_enabled: e.target.checked })}
                    data-testid="setting-dispute-enabled"
                  />
                  <span>Auto-open a dispute case whenever a merchant clicks <b>Never received</b>.</span>
                </label>
              </div>
            </div>
          </div>

          {/* SMS via Twilio */}
          <div className="border-t border-border pt-3 mt-2">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase tracking-wider font-bold text-muted-foreground">SMS mirror (Twilio)</h4>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={auditSettings.sms_enabled ?? false}
                  onChange={(e) => setAuditSettings({ ...auditSettings, sms_enabled: e.target.checked })}
                  data-testid="setting-sms-enabled"
                />
                <span>Send SMS alongside recall email when merchant has a phone</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Twilio Account SID</Label>
                <Input type="text" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={auditSettings.twilio_account_sid || ''}
                  onChange={(e) => setAuditSettings({ ...auditSettings, twilio_account_sid: e.target.value })}
                  data-testid="setting-twilio-sid" />
              </div>
              <div>
                <Label>Twilio Auth Token</Label>
                <Input type="password" placeholder="Kept server-side · masked after save"
                  value={auditSettings.twilio_auth_token || ''}
                  onChange={(e) => setAuditSettings({ ...auditSettings, twilio_auth_token: e.target.value })}
                  data-testid="setting-twilio-token" />
              </div>
              <div>
                <Label>Twilio From number (E.164)</Label>
                <Input type="tel" placeholder="+61…"
                  value={auditSettings.twilio_from_number || ''}
                  onChange={(e) => setAuditSettings({ ...auditSettings, twilio_from_number: e.target.value })}
                  data-testid="setting-twilio-from" />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Get credentials at <a className="underline" target="_blank" rel="noopener noreferrer" href="https://console.twilio.com/">Twilio Console</a>. Each tenant uses their own account so SMS never leaks between customers.</p>
          </div>
        </section>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Pallet types</h2>
          <p className="text-xs text-muted-foreground">
            Wooden, CHEP and Loscam ship as defaults. Add your own pooled or owned types.
          </p>
        </div>
        <Button onClick={beginCreate} data-testid="pallet-type-create-btn" size="sm">
          <Plus size={14} className="mr-1" /> New type
        </Button>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left">
              <th className="p-2 w-8"></th>
              <th className="p-2">Code</th>
              <th className="p-2">Name</th>
              <th className="p-2">Pooled?</th>
              <th className="p-2 text-right">Daily hire rate</th>
              <th className="p-2">Status</th>
              <th className="p-2 w-40 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {types.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                No pallet types yet — click &quot;New type&quot; or hit Seed defaults.
              </td></tr>
            )}
            {types.map((t) => (
              <tr key={t.id}
                data-testid={`pallet-type-row-${t.code}`}
                className="border-b border-border last:border-b-0 hover:bg-muted/30">
                <td className="p-2">
                  <span style={{ background: t.color || '#475569' }} className="inline-block w-3 h-3 rounded-full" />
                </td>
                <td className="p-2 font-mono font-semibold">{t.code}</td>
                <td className="p-2">
                  {t.name}
                  {t.is_seed && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-200">Seed</span>}
                </td>
                <td className="p-2">
                  {t.is_pooled
                    ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-100 text-blue-800">Pooled</span>
                    : <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800">Owned</span>}
                </td>
                <td className="p-2 text-right font-mono">{t.is_pooled ? `$${Number(t.daily_hire_rate || 0).toFixed(4)}` : '—'}</td>
                <td className="p-2">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${t.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => beginEdit(t)} data-testid={`pallet-type-edit-${t.code}`}>
                    <PencilSimple size={12} />
                  </Button>
                  <Button size="sm" variant="outline" className="ml-1" onClick={() => toggleActive(t)} data-testid={`pallet-type-toggle-${t.code}`}>
                    {t.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  {!t.is_seed && (
                    <Button size="sm" variant="outline" className="ml-1 text-rose-700"
                      onClick={() => onDelete(t)} data-testid={`pallet-type-delete-${t.code}`}>
                      <Trash size={12} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="pallet-type-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New pallet type'}</DialogTitle>
            <DialogDescription>
              Pooled types accrue daily hire — owned types track stock only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <Label>Code (uppercase, 2–24 chars) *</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="pallet-type-form-code" />
              </div>
            )}
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pallet-type-form-name" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_pooled}
                onChange={(e) => setForm({ ...form, is_pooled: e.target.checked })}
                data-testid="pallet-type-form-pooled" />
              <span>Pooled (CHEP / Loscam–style daily hire)</span>
            </label>
            {form.is_pooled && (
              <div>
                <Label>Daily hire rate ($/pallet/day)</Label>
                <Input type="number" step="0.0001" min="0" value={form.daily_hire_rate}
                  onChange={(e) => setForm({ ...form, daily_hire_rate: e.target.value })}
                  data-testid="pallet-type-form-rate" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  The nightly scheduler multiplies this by the per-merchant outstanding balance and posts a charge under 3PL Billing.
                </p>
              </div>
            )}
            {form.is_pooled && (
              <div>
                <Label>Claim email (e.g. claims@chep.com.au)</Label>
                <Input type="email" value={form.claim_email}
                  onChange={(e) => setForm({ ...form, claim_email: e.target.value })}
                  placeholder="claims@pooler.example.com"
                  data-testid="pallet-type-form-claim-email" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Used as the default recipient when sending a loss/damage claim PDF by email.
                </p>
              </div>
            )}
            <div>
              <Label>Colour (optional)</Label>
              <Input type="color" value={form.color || '#475569'}
                onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-20"
                data-testid="pallet-type-form-color" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="pallet-type-form-submit">
              {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create type')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
